import React, {useCallback} from 'react'
import {useState} from 'react'
import Button from 'components/Button'
import {useFunction} from 'utils/hooks/function'
import services from 'services'
import {BlobUpload, Optional, Project} from 'data/types'
import {useForm} from 'utils/hooks/form'
import EditableText from 'components/EditableText'
import Portal from 'components/Portal'
import i18n from 'i18n'
import AddProblemIcon from '../../assets/add-problem.svg'
import cross from 'assets/cross.svg'
import Dropzone from 'components/Dropzone'
import {readFile} from 'utils'
import {Col, Row} from 'components/Display'
import getImageSrcFromBlob from 'utils/blob'
import {AutoSizer} from 'react-virtualized'
import {noImagePlaceholderBlob} from 'utils/noImagePlaceholderBlob'

export default function Icon(props: {project: Project}) {
  const [isModalOpen, setModalOpen] = useState(false)
  const formData = useForm({
    title: '',
    description: '',
    isEditingTitle: true,
    isEditingDescription: true,
    blob: null as Optional<BlobUpload>,
  })

  const problemService = services.getProblemService(props.project.id.toString())
  const submitProblem = useFunction(problemService.createProblem)

  const onDrop = useCallback(
    (acceptedFiles) => {
      // this callback will be called after files get dropped, we will get the acceptedFiles. If you want, you can even access the rejected files too
      // Loop through accepted files
      acceptedFiles.map((file: any) => readFile(file).then((content) => formData.update('blob', content)))
    },
    [formData]
  )

  return (
    <React.Fragment>
      <Button
        className="bg-itx-primary-4 hover:bg-itx-primary-3 pt-0 items-baseline border-none h-8 flex-shrink-0"
        content={
          <Row className="items-center">
            <img className="h-4 w-4 object-cover items-center" src={AddProblemIcon} alt={'AddProblem'} />
            <div className="pl-2 text-white">Add problem</div>
          </Row>
        }
        onClick={() => setModalOpen(true)}
      />

      {isModalOpen && (
        <Portal>
          <div className="flex flex-col items-center problem-solution-modal justify-center absolute top-0 left-0 w-full h-full">
            <div className="flex flex-col justify-between w-6/12 h-1/5 problem-solution-modal-content shadow-lg bg-white rounded-lg">
              <div className="flex flex-row justify-between border-b border-gray-itx-border pl-8 pr-6 py-6">
                <div className="text-base font-semibold text-gray-itx3">{i18n.t('problems.newProblem')}</div>
                <img className={`w-4 h-4 cursor-pointer`} alt={'cross'} src={cross} onClick={() => setModalOpen(false)} />
              </div>
              <div className="flex flex-1 flex-col px-8 py-2">
                <EditableText
                  placeholder={i18n.t('problems.problemTitle')}
                  value={formData.state.title}
                  onChange={(title) => formData.update('title', title)}
                  editable={formData.state.isEditingTitle}
                  updateEditMode={(mode) => formData.update('isEditingTitle', mode)}
                  autoFocus
                  className="text-xl font-medium font-sans text-primary-darkest placeholder-itx-gray-base mt-2 mb-4 w-full"
                />
                {/* // We pass onDrop function and accept prop to the component. It will be used as initial params for useDropzone hook */}
                <Dropzone
                  onDrop={onDrop}
                  accept={['image/jpeg', 'image/png', 'image/gif']}
                  blob={formData.state.blob}
                  dragRejectText={i18n.t('problems.dragRejectText')}
                  dragAcceptText={i18n.t('problems.dragAcceptText')}
                  releaseToDropText={i18n.t('problems.releaseToDropText')}
                >
                  {(blob) => {
                    return (
                      <Col>
                        <img alt="addProblemImage" className="w-full object-cover" style={{maxHeight: '212px'}} src={getImageSrcFromBlob(blob)} />
                        <AutoSizer className="absolute">
                          {(size) => {
                            return (
                              <Row style={size} className="justify-center items-center">
                                <Button className="text-white bg-itx-primary-3 border-itx-primary-3" content="Switch image" />
                              </Row>
                            )
                          }}
                        </AutoSizer>
                      </Col>
                    )
                  }}
                </Dropzone>

                <EditableText
                  placeholder={i18n.t('problems.writeDescription')}
                  value={formData.state.description}
                  onChange={(desc) => formData.update('description', desc)}
                  editable={formData.state.isEditingDescription}
                  updateEditMode={(mode) => formData.update('isEditingDescription', mode)}
                  className="font-medium font-sans text-primary-darkest placeholder-itx-gray-base mt-4 outline-none py-4 resize-none "
                  isParagraph
                />
              </div>
              <div className="flex flex-row border-t border-gray-300 pt-2 px-2 pb-2 justify-end">
                <Button
                  className="my-4 text-gray-itx border-2 border-gray-300 rounded py-2"
                  content={i18n.t('misc.cancel')}
                  onClick={() => setModalOpen(false)}
                />
                <Button
                  loading={submitProblem.isRunning}
                  disabled={!formData.state.title || submitProblem.isRunning}
                  className="my-4 mr-6 ml-2 py-2 bg-itx-primary-4 hover:bg-itx-primary-3 text-white "
                  content={i18n.t('problems.createProblem')}
                  onClick={() =>
                    submitProblem({
                      projectId: props.project.id,
                      title: formData.state.title,
                      description: formData.state.description,
                      image: formData.state.blob ?? noImagePlaceholderBlob,
                    }).then(() => {
                      formData.clear()
                      setModalOpen(false)
                    })
                  }
                />
              </div>
            </div>
          </div>
        </Portal>
      )}
    </React.Fragment>
  )
}
